/**
 * AI Service for providing additional information about menu items and other content
 * Uses Puter AI to analyze and provide detailed information
 */

class AIService {
    constructor() {
        this.isInitialized = false;
        this.initializePuter();
    }

    async initializePuter() {
        try {
            // Check if localStorage is available
            if (typeof localStorage === 'undefined') {
                console.warn('localStorage not available, skipping Puter initialization');
                this.isInitialized = true;
                return;
            }

            // Load Puter AI SDK from CDN if not already loaded
            if (typeof puter === 'undefined') {
                await this.loadPuterSDK();
            }
            
            // Additional check to ensure puter is properly loaded
            if (typeof puter !== 'undefined' && puter.auth) {
                this.isInitialized = true;
            } else {
                console.warn('Puter SDK loaded but not fully initialized');
                this.isInitialized = true; // Mark as initialized to prevent blocking
            }
        } catch (error) {
            console.error('Failed to initialize Puter AI:', error);
            this.isInitialized = true; // Mark as initialized to prevent blocking
        }
    }

    loadPuterSDK() {
        return new Promise((resolve, reject) => {
            if (document.querySelector('script[src*="js.puter.com"]')) {
                resolve();
                return;
            }

            const script = document.createElement('script');
            script.src = 'https://js.puter.com/v2/';
            script.onload = () => {
                // Add a small delay to ensure SDK is fully loaded
                setTimeout(() => {
                    try {
                        // Test if puter is accessible
                        if (typeof puter !== 'undefined') {
                            resolve();
                        } else {
                            reject(new Error('Puter SDK loaded but not accessible'));
                        }
                    } catch (error) {
                        console.warn('Puter SDK initialization warning:', error);
                        resolve(); // Continue anyway
                    }
                }, 100);
            };
            script.onerror = reject;
            document.head.appendChild(script);
        });
    }

    async waitForInitialization() {
        if (this.isInitialized) return;
        
        // Wait up to 10 seconds for initialization
        for (let i = 0; i < 100; i++) {
            await new Promise(resolve => setTimeout(resolve, 100));
            if (this.isInitialized) return;
        }
        
        throw new Error('Puter AI initialization timeout');
    }

    /**
     * Get detailed information about a menu item
     */
    async getMenuItemInfo(menuItem) {
        try {
            // Try backend API first (faster and cached)
            const backendResponse = await this.callBackendAPI(`/ai/menu/${menuItem.id}/info`);
            if (backendResponse.success) {
                return backendResponse;
            }
        } catch (error) {
            console.log('Backend AI API not available, falling back to Puter AI');
        }

        // Fallback to Puter AI
        await this.waitForInitialization();

        const prompt = this.buildMenuItemPrompt(menuItem);
        
        try {
            const response = await puter.ai.chat(prompt, {
                model: 'gpt-4o-mini', // Use a cost-effective model
                max_tokens: 500,
                temperature: 0.7
            });

            return {
                success: true,
                data: response.message.content,
                menuItem: menuItem
            };
        } catch (error) {
            console.error('AI service error:', error);
            return {
                success: false,
                error: error.message,
                menuItem: menuItem
            };
        }
    }

    /**
     * Get nutritional information about a menu item
     */
    async getNutritionalInfo(menuItem) {
        try {
            // Try backend API first
            const backendResponse = await this.callBackendAPI(`/ai/menu/${menuItem.id}/nutrition`);
            if (backendResponse.success) {
                return backendResponse;
            }
        } catch (error) {
            console.log('Backend AI API not available, falling back to Puter AI');
        }

        // Fallback to Puter AI
        await this.waitForInitialization();

        const prompt = this.buildNutritionPrompt(menuItem);
        
        try {
            const response = await puter.ai.chat(prompt, {
                model: 'gpt-4o-mini',
                max_tokens: 400,
                temperature: 0.5
            });

            return {
                success: true,
                data: response.message.content,
                menuItem: menuItem
            };
        } catch (error) {
            console.error('AI nutrition service error:', error);
            return {
                success: false,
                error: error.message,
                menuItem: menuItem
            };
        }
    }

    /**
     * Get cooking tips and preparation suggestions
     */
    async getCookingTips(menuItem) {
        await this.waitForInitialization();

        const prompt = this.buildCookingTipsPrompt(menuItem);
        
        try {
            const response = await puter.ai.chat(prompt, {
                model: 'gpt-4o-mini',
                max_tokens: 400,
                temperature: 0.6
            });

            return {
                success: true,
                data: response.message.content,
                menuItem: menuItem
            };
        } catch (error) {
            console.error('AI cooking tips service error:', error);
            return {
                success: false,
                error: error.message,
                menuItem: menuItem
            };
        }
    }

    /**
     * Get ingredient analysis and substitutions
     */
    async getIngredientAnalysis(menuItem) {
        await this.waitForInitialization();

        const prompt = this.buildIngredientPrompt(menuItem);
        
        try {
            const response = await puter.ai.chat(prompt, {
                model: 'gpt-4o-mini',
                max_tokens: 400,
                temperature: 0.5
            });

            return {
                success: true,
                data: response.message.content,
                menuItem: menuItem
            };
        } catch (error) {
            console.error('AI ingredient analysis error:', error);
            return {
                success: false,
                error: error.message,
                menuItem: menuItem
            };
        }
    }

    /**
     * Get dietary recommendations and alternatives
     */
    async getDietaryRecommendations(menuItem, dietaryRestrictions = []) {
        await this.waitForInitialization();

        const prompt = this.buildDietaryPrompt(menuItem, dietaryRestrictions);
        
        try {
            const response = await puter.ai.chat(prompt, {
                model: 'gpt-4o-mini',
                max_tokens: 400,
                temperature: 0.6
            });

            return {
                success: true,
                data: response.message.content,
                menuItem: menuItem
            };
        } catch (error) {
            console.error('AI dietary recommendations error:', error);
            return {
                success: false,
                error: error.message,
                menuItem: menuItem
            };
        }
    }

    // Prompt builders
    buildMenuItemPrompt(menuItem) {
        return `Please provide detailed information about this menu item:

Name: ${menuItem.name}
Description: ${menuItem.description || 'No description provided'}
Price: ₹${menuItem.price}
Category: ${menuItem.category?.name || 'Unknown'}
Ingredients: ${menuItem.ingredients ? menuItem.ingredients.join(', ') : 'Not specified'}
Dietary Info: ${this.getDietaryInfo(menuItem)}
Preparation Time: ${menuItem.preparation_time} minutes
Spice Level: ${menuItem.spice_level}/5

Please provide:
1. A brief overview of this dish
2. Its cultural or regional significance
3. Key flavors and taste profile
4. Best occasions to enjoy this dish
5. Any interesting facts or history

Keep the response informative but concise (2-3 paragraphs).`;
    }

    buildNutritionPrompt(menuItem) {
        return `Analyze the nutritional content of this menu item:

Name: ${menuItem.name}
Ingredients: ${menuItem.ingredients ? menuItem.ingredients.join(', ') : 'Not specified'}
Dietary Info: ${this.getDietaryInfo(menuItem)}
Portions per person: ${menuItem.portions_per_person}

Please provide:
1. Estimated calories per serving
2. Key macronutrients (protein, carbs, fats)
3. Notable vitamins and minerals
4. Health benefits
5. Any nutritional concerns or considerations

Base your analysis on the ingredients and dietary information provided.`;
    }

    buildCookingTipsPrompt(menuItem) {
        return `Provide cooking tips and preparation suggestions for this menu item:

Name: ${menuItem.name}
Ingredients: ${menuItem.ingredients ? menuItem.ingredients.join(', ') : 'Not specified'}
Preparation Time: ${menuItem.preparation_time} minutes
Spice Level: ${menuItem.spice_level}/5

Please provide:
1. Key cooking techniques for this dish
2. Tips for achieving the best flavor
3. Common mistakes to avoid
4. How to adjust spice level
5. Serving suggestions and accompaniments

Keep tips practical and actionable.`;
    }

    buildIngredientPrompt(menuItem) {
        return `Analyze the ingredients of this menu item:

Name: ${menuItem.name}
Ingredients: ${menuItem.ingredients ? menuItem.ingredients.join(', ') : 'Not specified'}

Please provide:
1. Key ingredients and their roles in the dish
2. Possible ingredient substitutions for dietary restrictions
3. Ingredient quality tips (what to look for when buying)
4. Storage and handling tips for key ingredients
5. Any allergens or sensitivities to be aware of

Focus on practical information for home cooks.`;
    }

    buildDietaryPrompt(menuItem, dietaryRestrictions) {
        const restrictions = dietaryRestrictions.length > 0 ? dietaryRestrictions.join(', ') : 'general dietary preferences';
        
        return `Provide dietary recommendations for this menu item:

Name: ${menuItem.name}
Ingredients: ${menuItem.ingredients ? menuItem.ingredients.join(', ') : 'Not specified'}
Current Dietary Info: ${this.getDietaryInfo(menuItem)}
User's Dietary Restrictions: ${restrictions}

Please provide:
1. How well this dish fits the dietary restrictions
2. Suggested modifications to make it more suitable
3. Alternative dishes that might be better options
4. What to ask the restaurant about this dish
5. Any hidden ingredients to be aware of

Be helpful and specific in your recommendations.`;
    }

    getDietaryInfo(menuItem) {
        const dietary = [];
        if (menuItem.is_vegetarian) dietary.push('Vegetarian');
        if (menuItem.is_vegan) dietary.push('Vegan');
        if (menuItem.is_gluten_free) dietary.push('Gluten-free');
        if (menuItem.is_spicy) dietary.push(`Spicy (Level ${menuItem.spice_level}/5)`);
        
        return dietary.length > 0 ? dietary.join(', ') : 'No specific dietary information';
    }

    /**
     * Generic AI chat for custom queries
     */
    async chatWithAI(prompt, context = {}) {
        await this.waitForInitialization();

        try {
            // Check if puter is available before using it
            if (typeof puter === 'undefined' || !puter.ai) {
                throw new Error('Puter AI not available');
            }
            
            const response = await puter.ai.chat(prompt, {
                model: 'gpt-4o-mini',
                max_tokens: 500,
                temperature: 0.7
            });

            return {
                success: true,
                data: response.message.content,
                context: context
            };
        } catch (error) {
            console.error('AI chat error:', error);
            
            // Provide fallback response for common queries
            if (prompt.toLowerCase().includes('ingredients') || prompt.toLowerCase().includes('dietary')) {
                return {
                    success: true,
                    data: "I'm here to help with food-related questions! For specific ingredient and dietary information, please contact our support team.",
                    context: context
                };
            }
            
            return {
                success: false,
                error: error.message,
                context: context
            };
        }
    }

    /**
     * Call backend AI API
     */
    async callBackendAPI(endpoint, options = {}) {
        const baseURL = process.env.REACT_APP_API_URL || window.location.origin;
        const url = `${baseURL}/api${endpoint}`;
        
        const response = await fetch(url, {
            method: options.method || 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                ...options.headers
            },
            body: options.body ? JSON.stringify(options.body) : undefined
        });

        if (!response.ok) {
            throw new Error(`Backend API error: ${response.status}`);
        }

        return await response.json();
    }
}

// Create singleton instance
const aiService = new AIService();

export default aiService;
